//* External
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';

//* App Custom
import Input from './Input/Input';
import InputCheckbox from './InputCheckbox/InputCheckbox';
import InputDateRange from './InputDateRange/InputDateRange';
import InputDropzone from './InputDropzone/InputDropzone';
import InputSelect from './InputSelect/InputSelect';
import InputTextArea from './InputTextArea/InputTextArea';

const InputHandler = ({
  type,
  name,
  nameStartDate,
  nameEndDate,
  label,
  labelHelper,
  placeholder,
  iconName,
  isLoading,
  filePreSelected,
  iconColor,
  step,
  disabled = false,
  isMulti = false,
  link,
  design,
  defaultValue,
  rules = {},
  options = [],
  extensions = [],
  control,
  min,
  max,
  onInputChange = () => {},
  onEventChange = () => {},
  onFocus = () => {},
  onBlur = () => {}
}) => {
  let valueByDefault = defaultValue;
  if (type === 'select') {
    if (Array.isArray(defaultValue)) {
      valueByDefault = defaultValue.map((item) => item.value).join(',');
    } else if (defaultValue) valueByDefault = defaultValue.value;
  } else if (type === 'date-range') {
    const valueSplitted = defaultValue ? defaultValue.split(',') : '';
    valueByDefault = defaultValue && {
      [nameStartDate]: valueSplitted[0],
      [nameEndDate]: valueSplitted[1]
    };
  }

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      shouldUnregister={true}
      defaultValue={valueByDefault}
      render={({ field: { onChange }, fieldState: { error } }) => {
        let InputDisplayed;
        switch (type) {
          case 'text':
          case 'number':
          case 'date':
          case 'datetime-local':
          case 'time':
          case 'password':
            InputDisplayed = (
              <Input
                type={type}
                name={name}
                label={label}
                labelHelper={labelHelper}
                placeholder={placeholder}
                defaultValue={defaultValue}
                iconName={iconName}
                iconColor={iconColor}
                disabled={disabled}
                design={design}
                step={step}
                min={min}
                max={max}
                required={rules?.required?.value || false}
                hookError={error}
                hookOnChange={onChange}
                onInputChange={onInputChange}
                onFocus={onFocus}
                onBlur={onBlur}
              />
            );
            break;
          case 'checkbox':
            InputDisplayed = (
              <InputCheckbox
                name={name}
                label={label}
                labelHelper={labelHelper}
                placeholder={placeholder}
                defaultValue={defaultValue}
                disabled={disabled}
                required={rules?.required?.value || false}
                design={design}
                link={link}
                hookError={error}
                hookOnChange={onChange}
                onInputChange={onInputChange}
              />
            );
            break;
          case 'text-area':
            InputDisplayed = (
              <InputTextArea
                name={name}
                label={label}
                labelHelper={labelHelper}
                placeholder={placeholder}
                defaultValue={defaultValue}
                disabled={disabled}
                required={rules?.required?.value || false}
                design={design}
                hookError={error}
                hookOnChange={onChange}
                onInputChange={onInputChange}
                onFocus={onFocus}
                onBlur={onBlur}
              />
            );
            break;
          case 'select':
            InputDisplayed = (
              <InputSelect
                name={name}
                label={label}
                labelHelper={labelHelper}
                placeholder={placeholder}
                options={options}
                isMulti={isMulti}
                defaultValue={defaultValue}
                iconName={iconName}
                iconColor={iconColor}
                disabled={disabled}
                required={rules?.required?.value || false}
                design={design}
                hookError={error}
                hookOnChange={onChange}
                onInputChange={onInputChange}
                onEventChange={onEventChange}
                onFocus={onFocus}
                onBlur={onBlur}
              />
            );
            break;
          case 'dropzone':
            InputDisplayed = (
              <InputDropzone
                name={name}
                label={label}
                labelHelper={labelHelper}
                extensions={extensions}
                disabled={disabled}
                required={rules?.required?.value || false}
                design={design}
                hookError={error}
                hookOnChange={onChange}
                onInputChange={onInputChange}
                isLoading={isLoading}
                defaultValue={defaultValue}
                filePreSelected={filePreSelected}
              />
            );
            break;
          case 'date-range':
            InputDisplayed = (
              <InputDateRange
                name={name}
                label={label}
                labelHelper={labelHelper}
                defaultValueStartDate={
                  defaultValue ? defaultValue.split(',')[0] : undefined
                }
                defaultValueEndDate={
                  defaultValue ? defaultValue.split(',')[1] : undefined
                }
                nameStartDate={nameStartDate}
                nameEndDate={nameEndDate}
                min={min}
                max={max}
                disabled={disabled}
                design={design}
                required={rules?.required?.value || false}
                hookError={error}
                hookOnChange={onChange}
                onInputChange={onInputChange}
              />
            );
            break;
          default:
            return (InputDisplayed = <></>);
        }
        return InputDisplayed;
      }}
    />
  );
};

InputHandler.propTypes = {
  type: PropTypes.oneOf([
    'text',
    'number',
    'date',
    'datetime-local',
    'password',
    'checkbox',
    'text-area',
    'select',
    'dropzone',
    'time',
    'date-range'
  ]).isRequired,
  name: PropTypes.string.isRequired,
  nameStartDate: PropTypes.string,
  nameEndDate: PropTypes.string,
  label: PropTypes.string,
  labelHelper: PropTypes.string,
  placeholder: PropTypes.string,
  iconName: PropTypes.string,
  iconColor: PropTypes.string,
  disabled: PropTypes.bool,
  isMulti: PropTypes.bool,
  link: PropTypes.string,
  design: PropTypes.string,
  defaultValue: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
    PropTypes.string,
    PropTypes.number
  ]),
  rules: PropTypes.object,
  options: PropTypes.arrayOf(
    PropTypes.exact({
      label: PropTypes.string,
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.object
      ])
    })
  ),
  extensions: PropTypes.arrayOf(PropTypes.string),
  control: PropTypes.any.isRequired,
  onInputChange: PropTypes.func,
  onEventChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  step: PropTypes.string,
  min: PropTypes.string,
  max: PropTypes.string,
  isLoading: PropTypes.bool,
  filePreSelected: PropTypes.object
};

export default InputHandler;
